import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Search } from '@mui/icons-material';
import moment from 'moment-timezone';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import RowTypography from 'components/Table/RowTypography';
import StatusChip from 'components/Table/StatusChip';
import DataInfoSecondary from 'components/Widgets/DataInfoSecondary';
import DataInfoColorWidget from 'components/Widgets/DataInfoColorWidget';
import { colorsDark } from 'assets/theme/colorUtil';
import CustomTextField from 'components/Inputs/CustomTextField';
import { CustomizedDataGrid } from '../customized_data_grid';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { ReactComponent as AddIcon } from '../../assets/plus-icon.svg';
import { checkRole } from 'utils/roleUtils';
import { TIMEZONE_MAP } from 'utils/common-util';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { ReactComponent as InfoGreyIcon } from '../../assets/icon-grey-info.svg';
import { calculateRange } from 'components/Dropdown/DateTimeRangeFilter/useDateTimeRangePicker';
import { exportIbpStageList, getGdoKpi, getStageList, fetchIbpGdoList } from 'service/service';
import './dataPipeline.scss';

const GdoDetails = () => {
  const theme = useTheme();
  const params = useParams();
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

  const gdoName = decodeURIComponent(params.gdoName || 'GDO');
  const programId = params.programId || '';
  const gdoId = programId || 'gdo-default';
  const [gdoKpiData, setGdoKpiData] = useState<any>(null);
  const [loadingGdoKpi, setLoadingGdoKpi] = useState(false);
  const autoRefreshTimerRef = useRef<number | null>(null);
  const refreshDataRef = useRef<(() => Promise<void>) | null>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('IBPAutoRefresh');
    if (saved === null) return 5;
    const parsed = Number(saved);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
  });

  const parseInterval = (value: string | null): number | null => {
    if (value === null) return 5;
    if (value === '0') return null;
    const num = Number(value);
    return Number.isNaN(num) || num <= 0 ? null : num;
  };

  const buildStageDetailsUrl = (row: any) => {
    const stageId = row?.projectId || row?.Stage_Id || row?.stageId || 'stage-default';
    const stageName = row?.projectName || row?.Stage || 'Stage';

    // Use the IBP date-range selector values where available
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const { startDate: resolvedStartDate, endDate: resolvedEndDate } = getIbpDateRangeDates();

    // Map saved range type to a friendly dateFilter value expected by the details page
    const dateFilter = savedRangeType === 'today' ? 'Today' : savedRangeType === 'custom' ? 'Custom' : savedRangeType;

    // Use the IBP dashboard stage-details route (registered in DataPipeline routes)
    return `/data-pipeline/ibpDashboard/stage-details/${encodeURIComponent(gdoId)}/${encodeURIComponent(stageId)}/${encodeURIComponent(gdoName)}/${encodeURIComponent(stageName)}?startingDate=${resolvedStartDate}&endingDate=${resolvedEndDate}&dateFilter=${dateFilter}`;
  };

  const [startDate, setStartDate] = useState(moment().tz('America/Chicago').startOf('day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().tz('America/Chicago').endOf('day').format('YYYY-MM-DD'));
  const [filterText, setFilterText] = useState('');
  const [stageList, setStageList] = useState<any[]>([]);
  const [loadingStageList, setLoadingStageList] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0, currentPage: 1 });

  const gdoSummary = useMemo(() => [
    { title: 'Total Stages', value: gdoKpiData?.totalStages ?? 0, color: 'totalStages' },
    { title: 'Scheduled', value: gdoKpiData?.scheduledCount ?? 0, color: 'scheduled' },
    { title: 'In Progress', value: gdoKpiData?.inProgressCount ?? 0, color: 'inProgress' },
    { title: 'SLA Missed', value: gdoKpiData?.slaMissedCount ?? 0, color: 'slaMissed' },
    { title: 'SLA Met', value: gdoKpiData?.slaMetCount ?? 0, color: 'slaMet' },
  ], [gdoKpiData]);

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [filterInfo, setFilterInfo] = useState<any>({});
  const [filterDataOptions, setFilterDataOptions] = useState<any>({});
  const [searchDataValues, setSearchDataValues] = useState<any>({});

  const getWidgetClassName = (title: string): string => {
    const isActive = hoveredItem === title || clickedItem === title;

    if (isActive) {
      switch (title) {
        case 'Total Stages':
          return 'totalStages';
        case 'SLA Met':
          return 'slaMet';
        case 'SLA Missed':
          return 'slaMissed';
        case 'In Progress':
          return 'inProgress';
        case 'Scheduled':
          return 'scheduled';
        default:
          return 'none';
      }
    }

    return 'none';
  };

  const location = useLocation();

  // If URL contains startingDate/endingDate/dateFilter params (forward navigation), use them and persist to sessionStorage
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || window.location.search);
      const sd = sp.get('startingDate');
      const ed = sp.get('endingDate');
      const df = sp.get('dateFilter');
      if (sd && ed) {
        const iana = TIMEZONE_MAP.CST;
        // write canonical storage values
        if (df && df.toLowerCase() === 'custom') {
          sessionStorage.setItem('IBPDateRangeType', 'custom');
        } else if (df && df === 'Today') {
          sessionStorage.setItem('IBPDateRangeType', 'today');
        } else if (df) {
          sessionStorage.setItem('IBPDateRangeType', df);
        }
        sessionStorage.setItem('IBPDateRangeTimeZone', 'CST');
        sessionStorage.setItem('IBPStartDate', sd);
        sessionStorage.setItem('IBPEndDate', ed);
        setStartDate(sd);
        setEndDate(ed);
        // notify listeners
        window.dispatchEvent(new CustomEvent('IBPDateRangeApplied', { detail: { type: df, start: sd, end: ed } }));
        window.dispatchEvent(new Event('ibp-session-storage-updated'));
      }
    } catch (err) {
      // ignore
    }
  }, [location.search]);

  const handleMouseEnter = (title: string, value: any) => {
    if (value) {
      setHoveredItem(title);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleWidgetClick = (title: string, value: any) => {
    if (!value) return;
    setClickedItem(title);

    // When Total Stages is clicked, clear any SLA filters and reload the details table
    if (title === 'Total Stages') {
      setFilterInfo({});
      void fetchStageList({});
      return;
    }

    // For other cards, set the slaStatus filter and reload the details table
    // The backend expects an array for slaStatus in filter options
    const slaFilter = [title];
    const newFilter = { ...(filterInfo || {}), slaStatus: slaFilter };
    setFilterInfo(newFilter);
    void fetchStageList(newFilter);
  };

  const handleGridSorting = (nextSorting: any) => {
    setSortingInfo(nextSorting || { sortBy: '', sortDir: '' });
  };

  const handleGridFilter = (nextFilter: any) => {
    const clonedFilter = nextFilter ? JSON.parse(JSON.stringify(nextFilter)) : {};
    setFilterInfo(clonedFilter);

    // If grid 'Clear All Filters' triggered (all filter arrays empty), reset widget selection
    const isClearAll =
      clonedFilter &&
      Object.values(clonedFilter).every((v: any) => Array.isArray(v) && v.length === 0);
    if (isClearAll) {
      setClickedItem(null);
    }

    fetchStageList(clonedFilter);
  };

  const handleFilterSearch = (searchValue: string, columnKey: string) => {
    if (!searchValue || !columnKey) return;
    const currentOptions = filterDataOptions[columnKey] || [];
    const filteredOptions = currentOptions.filter((option: any) =>
      String(option).toLowerCase().includes(searchValue.toLowerCase())
    );
    setSearchDataValues({ name: filteredOptions });
  };

  const parseStageFilters = (filterState?: any) => {
    const filters: any = {};
    const source = filterState || filterInfo;
    if (source?.projectName?.length) filters.projectName = source.projectName;
    if (source?.origin?.length) filters.origin = source.origin;
    if (source?.destination?.length) filters.destination = source.destination;
    if (source?.slaStatus?.length) filters.slaStatus = source.slaStatus;
    return filters;
  };

  const getStageListPayload = (filterState?: any) => {
    const appliedFilter = filterState || filterInfo;
    return {
      paginationInfo: {
        page: 1,
        pageSize: 100,
      },
      searchText: filterText.trim(),
      sortingInfo: {
        sortBy: sortingInfo.sortBy || 'projectName',
        sortDir: sortingInfo.sortDir || 'asc',
      },
      globalFilters: parseStageFilters(appliedFilter),
      dateRange: getIbpDateRange(),
      programId,
    };
  };

  const parseStageListFilterOptions = (filterOptions: any) => {
    if (!filterOptions || typeof filterOptions !== 'object') return {};
    return {
      projectName: filterOptions.projectName || filterOptions.Project_Name || [],
      origin: filterOptions.origin || [],
      destination: filterOptions.destination || [],
      slaStatus: filterOptions.slaStatus || filterOptions.SLA_Status || [],
    };
  };

  // ── GDO / stage roll-up statuses (from the gdoList endpoint) ───────────
  // The backend rolls job statuses up to stage & GDO level
  // (artifacts/roll up .txt): slaStatus ∈ {SLA Missed, Not Started,
  // In Progress, SLA Met}, executionStatus ∈ {Not Started, In Progress,
  // Completed}. This replaces the old per-stage job-execution prefetch.
  const [gdoRollup, setGdoRollup] = useState<any>(null);
  const [stageRollupMap, setStageRollupMap] = useState<
    Map<string, { slaStatus: string; executionStatus: string }>
  >(new Map());

  const loadGdoRollup = useCallback(async () => {
    if (!programId) return;
    try {
      const resp = await fetchIbpGdoList({
        paginationInfo: { page: 1, pageSize: 100 },
        sortingInfo: { sortBy: '', sortDir: '' },
        globalFilters: {},
        searchText: '',
        dateRange: getIbpDateRange(),
        programId,
      });
      const data = resp?.data ?? resp ?? {};
      const records = Array.isArray(data.records) ? data.records : [];
      if (!records.length) {
        setGdoRollup(null);
        setStageRollupMap(new Map());
        return;
      }

      // one record per day (ascending) → the last one is the latest day
      const latest = records.reduce((a: any, b: any) =>
        String(a?.gdoDate || '') >= String(b?.gdoDate || '') ? a : b
      );
      setGdoRollup(latest);

      // stage roll-ups keyed by (projectId, gdoDate) — the same day keys
      // getStageList uses for scheduleDate
      const map = new Map<string, { slaStatus: string; executionStatus: string }>();
      for (const rec of records) {
        for (const stage of rec?.projects || []) {
          if (!stage?.projectId) continue;
          map.set(`${stage.projectId}|${rec.gdoDate || ''}`, {
            slaStatus: stage.slaStatus || '',
            executionStatus: stage.executionStatus || '',
          });
        }
      }
      setStageRollupMap(map);
    } catch (err) {
      console.error('Failed to load GDO roll-up status', err);
      setGdoRollup(null);
      setStageRollupMap(new Map());
    }
  }, [programId, startDate, endDate]);

  // GDO-level Current Status: rolled-up SLA status from gdoList,
  // falling back to the raw program SLA status from getGdoKpi
  const currentStatusValue = useMemo(() => {
    const raw = gdoRollup?.slaStatus || gdoKpiData?.slaStatus || 'N/A';
    return String(raw).toUpperCase();
  }, [gdoRollup, gdoKpiData?.slaStatus]);

  // Merge the backend roll-ups into the stage rows (matched on
  // projectId + scheduleDate === gdoDate)
  const displayStageList = useMemo(() => {
    if (!stageRollupMap.size) return stageList;
    return stageList.map((row: any) => {
      const stageId = row?.projectId || row?.Stage_Id || row?.stageId;
      const roll = stageId
        ? stageRollupMap.get(`${stageId}|${row.scheduleDate || ''}`)
        : undefined;
      if (!roll) return row;
      return {
        ...row,
        slaStatus: roll.slaStatus || row.slaStatus,
        executionStatus: roll.executionStatus || row.executionStatus || '',
      };
    });
  }, [stageList, stageRollupMap]);

  const currentStatusTextColor = '#000000';

  const renderCurrentStatusValue = (status: string) => {
    if (!status || status === 'N/A' || status === '--') {
      return (
        <Typography variant="bodyValue" lineHeight="24px" sx={{ color: currentStatusTextColor, fontSize: '1rem', fontWeight: 500 }}>
          {status || 'N/A'}
        </Typography>
      );
    }

    let chipBg = '#B0B7C3';
    if (status === 'SLA MET') chipBg = 'rgba(1, 192, 8, 1)';
    else if (status === 'SLA MISSED') chipBg = 'rgba(189, 29, 64, 1)';
    else if (status === 'IN PROGRESS') chipBg = 'rgba(236, 185, 20, 1)';
    else if (status === 'NOT STARTED' || status === 'SCHEDULED' || status === 'NOT SCHEDULED') chipBg = 'rgba(169, 173, 203, 1)';

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          py: 0.5,
          borderRadius: '4px',
          backgroundColor: chipBg,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: '0.75rem',
          lineHeight: 1.2,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {status}
      </Box>
    );
  };

  const fetchStageList = useCallback(async (filterState?: any) => {
    setLoadingStageList(true);
    try {
      const result = await getStageList(getStageListPayload(filterState));
      const data = result?.data || result || {};
      if (data.filterOptions) {
        setFilterDataOptions(parseStageListFilterOptions(data.filterOptions));
      }
      const records = data.records && Array.isArray(data.records) ? data.records : [];
      setStageList(records);
      const totalElements = records.length;
      const totalPages = Math.ceil(totalElements / pageSize) || 1;
      setPaginationData({ totalElements, totalPages, currentPage: 1 });
      setPage(1);
    } catch (error) {
      console.error('Failed to fetch stage list', error);
      setStageList([]);
      setPaginationData({ totalElements: 0, totalPages: 0, currentPage: 1 });
      setPage(1);
    } finally {
      setLoadingStageList(false);
    }
  }, [filterText, sortingInfo, filterInfo, pageSize, programId, startDate, endDate]);

  const loadGdoKpi = useCallback(async () => {
    if (!programId || !gdoName) return;

    setLoadingGdoKpi(true);
    try {
      const payload = {
        programId,
        programName: gdoName,
        dateRange: getIbpDateRange(),
      };
      const resp = await getGdoKpi(payload);
      setGdoKpiData(resp?.data ?? resp);
    } catch (err) {
      setGdoKpiData(null);
    } finally {
      setLoadingGdoKpi(false);
    }
  }, [gdoName, programId, startDate, endDate]);

  const refreshData = useCallback(async () => {
    if (!programId || !gdoName) return;

    await Promise.all([fetchStageList(), loadGdoKpi(), loadGdoRollup()]);
  }, [fetchStageList, gdoName, loadGdoKpi, loadGdoRollup, programId]);

  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);

  useEffect(() => {
    if (!programId || !gdoName) return;

    const timer = window.setTimeout(() => {
      void refreshData();
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [programId, gdoName, startDate, endDate]);
  
  useEffect(() => {
    if (!programId || !gdoName) return;
    const timer = window.setTimeout(() => {
      void fetchStageList();
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [filterText, sortingInfo, filterInfo]);

  useEffect(() => {
    if (autoRefreshTimerRef.current) {
      window.clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }

    if (autoRefreshInterval === null || autoRefreshInterval <= 0) {
      return;
    }

    autoRefreshTimerRef.current = window.setInterval(() => {
      void refreshDataRef.current?.();
    }, autoRefreshInterval * 60 * 1000);

    return () => {
      if (autoRefreshTimerRef.current) {
        window.clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
    };
  }, [autoRefreshInterval, programId, gdoName]);

  const getCardTextColors = (_title: string) => ({ label: '#FFFFFF', value: '#FFFFFF' });

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    return moment(value).format('DD-MM-YYYY hh:mm A');
  };

  const gdoTooltipItems = useMemo(() => [
    { label: 'Data Owner', value: gdoKpiData?.dataOwner || '-' },
    { label: 'Data Steward ', value: gdoKpiData?.dataSteward || '-' },
    { label: 'Created At', value: formatDateTime(gdoKpiData?.createdAt) },
    { label: 'Updated At', value: formatDateTime(gdoKpiData?.updatedAt) },
  ], [gdoKpiData]);

  const getStatusChipStyle = (status: string) => {
    const normalized = status?.toLowerCase?.() || '';

    if (normalized.includes('sla met')) return { backgroundColor: 'rgba(1, 192, 8, 1)', color: '#FFFFFF' };
    if (normalized.includes('sla missed')) return { backgroundColor: 'rgba(189, 29, 64, 1)', color: '#FFFFFF' };
    if (normalized.includes('in progress')) return { backgroundColor: 'rgba(236, 185, 20, 1)', color: '#FFFFFF' };
    if (normalized.includes('not started') || normalized.includes('scheduled') || normalized.includes('not scheduled')) return { backgroundColor: 'rgba(169, 173, 203, 1)', color: '#FFFFFF' };

    return { backgroundColor: '#B0B7C3', color: '#FFFFFF' };
  };

  const getExecutionChipStyle = (status: string) => {
    const normalized = status?.toLowerCase?.() || '';

    if (normalized.includes('completed') || normalized.includes('success')) return { backgroundColor: 'rgba(1, 192, 8, 1)', color: '#FFFFFF' };
    if (normalized.includes('in progress') || normalized.includes('running')) return { backgroundColor: 'rgba(236, 185, 20, 1)', color: '#FFFFFF' };
    if (normalized.includes('not started') || normalized.includes('not executed')) return { backgroundColor: 'rgba(169, 173, 203, 1)', color: '#FFFFFF' };

    return { backgroundColor: '#B0B7C3', color: '#FFFFFF' };
  };

  const getPaginationSlice = (list: any[], currentPage: number, currentPageSize: number) => {
    const startIndex = (currentPage - 1) * currentPageSize;
    return list.slice(startIndex, startIndex + currentPageSize);
  };

  const handlePageChange = (_event: any, newPage: number) => {
    setPage(newPage);
    setPaginationData((prev: any) => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (event: any) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    setPage(1);
    const totalPages = Math.ceil(paginationData.totalElements / newPageSize) || 1;
    setPaginationData((prev: any) => ({ ...prev, totalPages, currentPage: 1 }));
  };

  const handleExportStageList = useCallback(async () => {
    try {
      await exportIbpStageList(getStageListPayload());
    } catch (error) {
      console.error('Failed to export stage list', error);
    }
  }, [filterText, sortingInfo, filterInfo, programId, startDate, endDate]);

  const gdoInfoTooltip = (
    <Tooltip
      arrow
      placement="bottom-start"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#EAF4FA !important',
            color: '#0B3A56',
            borderRadius: '8px',
            border: '1px solid rgba(11,58,86,0.12)',
            boxShadow: '0px 10px 24px rgba(13, 43, 67, 0.18)',
            px: 1.5,
            py: 1,
            minWidth: '220px',
            '& .MuiTooltip-arrow': {
              color: '#EAF4FA !important',
            },
          },
        },
      }}
      title={
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          {gdoTooltipItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                minWidth: '220px',
                lineHeight: 1.4,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  minWidth: 4,
                  borderRadius: '50%',
                  backgroundColor: '#000000',
                  mt: '0.45rem',
                }}
              />
              <Typography sx={{ color: '#000000', fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.5 }}>
                {item.label}: <Box component="span" sx={{ color: '#000000', fontWeight: 500 }}>{item.value}</Box>
              </Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
        <InfoGreyIcon />
      </Box>
    </Tooltip>
  );

  const navigate = useNavigate();

  const handleAddStageClick = () => {
    navigate(
      `/data-pipeline/ibpDashboard/gdoTabs?programID=${encodeURIComponent(programId)}&programName=${encodeURIComponent(gdoName)}&tab=1&type=Project&isEdited=true`,
    );
  };

  const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const getIbpDateRange = () => {
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
    const savedStartDate = sessionStorage.getItem('IBPStartDate');
    const savedEndDate = sessionStorage.getItem('IBPEndDate');
    const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

    const dateRange: any = {
      dateTimeRangeType: savedRangeType,
      timeZone: savedTimeZone,
    };

    if (savedRangeType === 'custom' && savedStartDate && savedEndDate) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(savedStartDate)) {
        dateRange.startTime = moment.tz(savedStartDate, 'YYYY-MM-DD', ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.startTime = moment(savedStartDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(savedEndDate)) {
        dateRange.endTime = moment.tz(savedEndDate, 'YYYY-MM-DD', ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.endTime = moment(savedEndDate).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      }
    } else {
      const range = calculateRange(savedRangeType, ianaTimezone);
      if (range?.from && range?.to) {
        dateRange.startTime = moment(range.from).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = savedRangeType === 'today'
          ? moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss')
          : moment(range.to).tz(ianaTimezone).format('DD-MM-YYYY HH:mm:ss');
      } else {
        dateRange.dateTimeRangeType = 'today';
        dateRange.startTime = moment.tz(ianaTimezone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
        dateRange.endTime = moment.tz(ianaTimezone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
      }
    }

    return dateRange;
  };

  const getIbpDateRangeDates = () => {
    const savedRangeType = sessionStorage.getItem('IBPDateRangeType') || 'today';
    const savedTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone') || 'CST';
    const savedStartDate = sessionStorage.getItem('IBPStartDate');
    const savedEndDate = sessionStorage.getItem('IBPEndDate');
    const ianaTimezone = TIMEZONE_MAP[savedTimeZone] || TIMEZONE_MAP.CST;

    if (savedRangeType === 'custom' && savedStartDate && savedEndDate) {
      const sd = /^\d{4}-\d{2}-\d{2}$/.test(savedStartDate)
        ? moment.tz(savedStartDate, 'YYYY-MM-DD', ianaTimezone)
        : moment(savedStartDate).tz(ianaTimezone);
      const ed = /^\d{4}-\d{2}-\d{2}$/.test(savedEndDate)
        ? moment.tz(savedEndDate, 'YYYY-MM-DD', ianaTimezone)
        : moment(savedEndDate).tz(ianaTimezone);
      return {
        startDate: sd.format('YYYY-MM-DD'),
        endDate: ed.format('YYYY-MM-DD'),
      };
    }

    const range = calculateRange(savedRangeType, ianaTimezone);
    if (range?.from && range?.to) {
      return {
        startDate: moment(range.from).tz(ianaTimezone).format('YYYY-MM-DD'),
        endDate: moment(range.to).tz(ianaTimezone).format('YYYY-MM-DD'),
      };
    }

    const today = moment.tz(ianaTimezone).format('YYYY-MM-DD');
    return { startDate: today, endDate: today };
  };

  useEffect(() => {
    const syncAutoRefresh = () => {
      const savedInterval = sessionStorage.getItem('IBPAutoRefresh');
      const parsed = parseInterval(savedInterval);
      setAutoRefreshInterval((prev) => (prev === parsed ? prev : parsed));
    };

    syncAutoRefresh();

    window.addEventListener('storage', syncAutoRefresh);
    window.addEventListener('ibp-session-storage-updated', syncAutoRefresh as EventListener);

    return () => {
      window.removeEventListener('storage', syncAutoRefresh);
      window.removeEventListener('ibp-session-storage-updated', syncAutoRefresh as EventListener);
    };
  }, []);

  useEffect(() => {
    const syncDateRange = () => {
      const { startDate: sd, endDate: ed } = getIbpDateRangeDates();
      setStartDate(sd);
      setEndDate(ed);
    };

    syncDateRange();

    const handleIbpDateRangeUpdated = () => {
      syncDateRange();
    };

    window.addEventListener('IBPDateRangeApplied', handleIbpDateRangeUpdated);
    window.addEventListener('ibp-session-storage-updated', handleIbpDateRangeUpdated);

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && (event.key.includes('IBPDateRange') || event.key.includes('IBPStartDate') || event.key.includes('IBPEndDate'))) {
        syncDateRange();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('IBPDateRangeApplied', handleIbpDateRangeUpdated);
      window.removeEventListener('ibp-session-storage-updated', handleIbpDateRangeUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleDetailsThreeDotsClose = (_event: unknown, _reason?: string) => {
    return undefined;
  };

  const handleAction = (action: string) => {
    if (action === 'edit') {
      return;
    }
    if (action === 'delete') {
      return;
    }
  };

  const detailsColumns = [
    {
      key: 'projectName',
      name: 'STAGE',
      formatter: ({ row }: any) => (
        <Link
          to={buildStageDetailsUrl(row)}
          state={row}
          title={row.projectName || 'Stage'}
        >
          <RowTypography sx={{ textAlign: 'center', width: '100%', color: '#009BD8', textDecoration: 'underline' }}>{row.projectName || '--'}</RowTypography>
        </Link>
      ),
      minWidth: 180,
    },
    {
      key: 'origin',
      name: 'ORIGIN',
      formatter: ({ row }: any) => (
        <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{row.origin || '--'}</RowTypography>
      ),
      minWidth: 180,
    },
    {
      key: 'destination',
      name: 'DESTINATION',
      formatter: ({ row }: any) => (
        <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{row.destination || '--'}</RowTypography>
      ),
      minWidth: 200,
    },
    {
      key: 'scheduleDate',
      name: 'SCHEDULED DATE',
      formatter: ({ row }: any) => (
        <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{row.scheduleDate || '--'}</RowTypography>
      ),
      minWidth: 200,
    },
    {
      key: 'startTime',
      name: 'START TIME (CST)',
      formatter: ({ row }: any) => {
        const value = row.startTime;
        const formatted = value ? moment.utc(value).tz('America/Chicago').format('hh:mm A') : '--';
        return <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{formatted}</RowTypography>;
      },
      minWidth: 230,
    },
    {
      key: 'endTime',
      name: 'END TIME (CST)',
      formatter: ({ row }: any) => {
        const value = row.endTime;
        const formatted = value ? moment.utc(value).tz('America/Chicago').format('hh:mm A') : '--';
        return <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{formatted}</RowTypography>;
      },
      minWidth: 230,
    },
    {
      key: 'slaStatus',
      name: 'SLA STATUS',
      align: 'center',
      headerAlign: 'center',
      formatter: ({ row }: any) => {
        // slaStatus carries the backend job→stage roll-up (displayStageList),
        // falling back to the raw ES stage status
        const status = row?.slaStatus || '--';
        const chipStyle = getStatusChipStyle(status);

        if (status === '--' || status === 'NA') {
          return <Box sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{status}</Box>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: '24px',
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              backgroundColor: chipStyle.backgroundColor,
              color: chipStyle.color,
              fontWeight: 700,
              fontSize: '0.68rem',
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {status}
          </Box>
        );
      },
      minWidth: 160,
    },
    {
      key: 'executionStatus',
      name: 'EXECUTION STATUS',
      align: 'center',
      headerAlign: 'center',
      formatter: ({ row }: any) => {
        // Rolled-up execution status of every job in the stage
        const status = row?.executionStatus || '--';
        const chipStyle = getExecutionChipStyle(status);

        if (status === '--') {
          return <Box sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{status}</Box>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: '24px',
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              backgroundColor: chipStyle.backgroundColor,
              color: chipStyle.color,
              fontWeight: 700,
              fontSize: '0.68rem',
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {status}
          </Box>
        );
      },
      minWidth: 160,
    },
    {
      key: 'nextExecutionTime',
      name: 'NEXT EXEC. TIME (CST)',
      formatter: ({ row }: any) => <RowTypography sx={{ textAlign: 'center', width: '100%' }}>{row.nextExecutionTime || '--'}</RowTypography>,
      minWidth: 230,
    },
  ];

  return (
    <PageWrapper>
      <Box className="main-bordered-container" bgcolor="backgroundLight.primary">
        <PageHeader
          icon={DashboardIcon}
          title={[
            {
              text: 'All GDOs',
              link: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
            },
            {
              text: gdoName,
            },
          ]}
        >
          
          {isAllowed && (
            <Button variant="pepwiseSecondary" startIcon={<AddIcon />} onClick={handleAddStageClick}>
              ADD STAGE
            </Button>
          )}
        </PageHeader>

        <Box p={3} pr={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <DataInfoSecondary
                bgColor="#fff"
                sx={{ border: '1px solid #E8EEF4', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)', borderRadius: '8px' }}
                infoCards={[
                  {
                    label: 'GDO Name',
                    value: gdoName,
                    labelProps: { sx: { color: '#71758F', fontSize: '0.8rem', fontWeight: 600 } },
                    valueProps: { sx: { color: '#000000', fontSize: '1rem', fontWeight: 500, lineHeight: '24px' } },
                  },
                  {
                    label: 'Current Status',
                    value: renderCurrentStatusValue(currentStatusValue),
                    labelProps: { sx: { color: '#71758F', fontSize: '0.8rem', fontWeight: 600 } },
                    valueProps: { sx: { color: currentStatusTextColor, fontSize: '1rem', fontWeight: 700, lineHeight: '24px' } },
                  },
                ]}
                infoTooltip={gdoInfoTooltip}
                infoOptionMenu={{
                  onClick: handleDetailsThreeDotsClick,
                  onClose: handleDetailsThreeDotsClose,
                  menuItems: [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: <EditIcon />, 
                      onMenuClick: () => handleAction('edit'),
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: <DeleteIcon />, 
                      onMenuClick: () => handleAction('delete'),
                    },
                  ],
                }}
                isAdmin={isAllowed}
              />
            </Box>

            <Box sx={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gdoSummary.map(({ title, value, color }) => {
                const textColors = getCardTextColors(title);
                const isActive = hoveredItem === title || clickedItem === title;
                const activeColor = (colorsDark.boxColor as any)[color as any];

                return (
                  <Box
                    key={title}
                    sx={{
                      flex: '1 1 calc(20% - 8px)',
                      minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 8px)', lg: 'calc(20% - 8px)' },
                      display: 'flex',
                    }}
                    onMouseEnter={() => handleMouseEnter(title, value)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleWidgetClick(title, value)}
                    className="DataInfo"
                  >
                    <DataInfoColorWidget
                      title={title}
                      className={getWidgetClassName(title)}
                      isActive={isActive}
                      onClick={() => handleWidgetClick(title, value)}
                      value={value}
                      boxColor={color}
                      isLoading={loadingGdoKpi}
                      labelProps={{ sx: { color: isActive && activeColor ? activeColor.bgColor : textColors.label, fontSize: '0.8rem', fontWeight: 600 } }}
                      valueProps={{ sx: { color: isActive && activeColor ? activeColor.bgColor : textColors.value, fontSize: '1.25rem', fontWeight: 700, lineHeight: '24px' } }}
                      sx={{
                        border: '1px solid #E8EEF4',
                        boxShadow: isActive ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
                        backgroundColor: isActive ? '#FFFFFF' : activeColor?.bgColor,
                        borderBottom: isActive && activeColor ? `7px solid ${activeColor.bgColor}` : undefined,
                        '& .colorIntimation': { display: 'none' },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <CustomizedDataGrid
              tableColumns={detailsColumns}
              tableData={getPaginationSlice(displayStageList, page, pageSize)}
              tableType="ibp-gdo-stage-details"
              defaultColumnNames={detailsColumns.map((column: any) => column.name)}
              onSorting={handleGridSorting}
              currSorting={sortingInfo}
              filterDataOptions={filterDataOptions}
              searchDataValues={searchDataValues}
              onFilterCheck={handleGridFilter}
              currFilter={filterInfo}
              onFilterSearch={handleFilterSearch}
              onFilterClick={() => {}}
              page={page}
              pageSize={pageSize}
              paginationData={paginationData}
              handlePageChange={handlePageChange}
              handlepageSizeChange={handlePageSizeChange}
              listLength={stageList.length}
              isFetching={loadingStageList}
              isRenderPagination={true}
              isShowMoreButton={true}
              showExport={true}
              exportTableData={handleExportStageList}
              onCellClick={() => {}}
              isChildren={true}
              childrenContainerProps={{ sx: { width: '100%', justifyContent: 'flex-end' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                <Typography
                  variant="overlayWidgetTitle"
                  sx={{ color: `${theme.palette.table.title} !important`, whiteSpace: 'nowrap' }}
                  component="span"
                >
                  Stages
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <CustomTextField
                    placeholder="Search stages"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{ width: '22rem', borderRadius: '4px', zIndex: '99' }}
                    sx={{
                      flexShrink: 0,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F6FBFD',
                        borderRadius: '4px',
                        '& fieldset': {
                          borderColor: '#6F8A99',
                        },
                        '&:hover fieldset': {
                          borderColor: '#6F8A99',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6F8A99',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#0B3A56',
                        padding: '14px',
                        paddingLeft: 0,
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#6F8A99',
                        opacity: 1,
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        backgroundColor: 'transparent',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#6F8A99' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        height: 36,
                      },
                    }}
                  />
                </Box>
              </Box>
            </CustomizedDataGrid>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default GdoDetails;
